import { Component, ElementRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NavigationService } from 'src/app/services/navigation.service';

/**
 * A component that displays a loading spinner/gallery for indicating loading states.
 * This component automatically handles its visibility based on the navigationService's galleryLoaderVisible$ observable.
 *
 * @remarks
 * The component manages its own visibility by manipulating the display style of its root element.
 * It automatically cleans up subscriptions when destroyed to prevent memory leaks.
 *
 * @example
 * ```html
 * <loading-gallery></loading-gallery>
 * ```
 */
@Component({
  selector: 'loading-gallery',
  templateUrl: './loading-gallery.component.html',
  styleUrls: ['./loading-gallery.component.scss'],
})
export class LoadingGalleryComponent implements OnDestroy {
  private subscription: Subscription;

  constructor(
    private navigationService: NavigationService,
    private elementRef: ElementRef<HTMLDivElement>,
  ) {
    // Subscribe to loading state changes and handle visibility
    this.subscription = this.navigationService.galleryLoaderVisible$.subscribe({
      next: (loading: boolean) => {
        try {
          this.elementRef.nativeElement.style.display = loading ? 'flex' : 'none';
        } catch (error) {
          console.error('Error updating loading gallery visibility:', error);
        }
      },
      error: (error) => {
        console.error('Error in gallery loader subscription:', error);
        // Ensure the loader is hidden in case of error
        try {
          this.elementRef.nativeElement.style.display = 'none';
        } catch (innerError) {
          console.error('Error hiding loader after subscription error:', innerError);
        }
      },
    });
  }

  /**
   * Cleanup method to prevent memory leaks.
   * Automatically called by Angular when the component is destroyed.
   */
  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
